import { X, TrendingUp, TrendingDown, AlertTriangle, Package, Calendar, DollarSign } from 'lucide-react'
import { useFormatters } from '../../contexts/PreferencesContext'
import { useQualityMeasureLabel, useQualityMeasureLongLabel } from '../../lib/qualityLabels'
import { getRowDrillUrl, rowDrillProps, ROW_DRILL_CLASS } from '../drilldown/drilldownUtils'

export interface DrillDownData {
  supplier_id: number
  supplier_name: string
  metric_type: 'ppm' | 'otif' | 'otd' | 'arrears'

  // Current performance (CR-8609: null = no rate, shown N/A)
  current_value: number | null
  previous_value?: number
  target?: number

  // Breakdown details
  details?: {
    // For PPM
    ncrs?: Array<{
      ncr_id: string
      ncr_number: string
      part_number: string
      qty_rejected: number
      date_raised: string
      status: string
      severity?: string
    }>

    // For OTIF/OTD
    late_deliveries?: Array<{
      po_number: string
      part_number: string
      due_date: string
      actual_date: string
      days_late: number
      qty: number
    }>

    // For Arrears
    arrears_lines?: Array<{
      po_number: string
      part_number: string
      qty_outstanding: number
      days_overdue: number
      /** CR-7714 BL-16: in the PO's own `currency`; null = unpriced. */
      value: number | null
      currency?: string | null
      due_date: string
    }>
  }

  // Summary stats
  summary?: {
    total_pos?: number
    total_lines?: number
    total_value?: number
    affected_parts?: number
  }
}

interface DrillDownModalProps {
  isOpen: boolean
  onClose: () => void
  data: DrillDownData | null
  currency?: string
}

export function DrillDownModal({ isOpen, onClose, data, currency = 'GBP' }: DrillDownModalProps) {
  const { formatCurrency: contextFormatCurrency, formatDate } = useFormatters()
  const qualityLabel = useQualityMeasureLabel()
  const qualityLongLabel = useQualityMeasureLongLabel()

  if (!isOpen || !data) return null

  // CR-7714 BL-16: a row carries its own currency; an unpriced row shows "—".
  const formatCurrency = (value: number | null | undefined, rowCurrency?: string | null) => {
    if (value === null || value === undefined) return '—'
    return contextFormatCurrency(value, rowCurrency || currency)
  }

  const getMetricLabel = () => {
    switch (data.metric_type) {
      case 'ppm': return `${qualityLongLabel} (${qualityLabel})`
      case 'otif': return 'On-Time In-Full (OTIF)'
      case 'otd': return 'On-Time Delivery (OTD)'
      case 'arrears': return 'Arrears Value'
      default: return 'Performance'
    }
  }

  const getMetricColor = () => {
    if (!data.target || data.current_value === null) return 'text-gray-600'

    const isGood = data.metric_type === 'ppm' || data.metric_type === 'arrears'
      ? data.current_value <= data.target
      : data.current_value >= data.target

    return isGood ? 'text-green-600' : 'text-red-600'
  }

  const getTrendIcon = () => {
    if (!data.previous_value || data.current_value === null) return null

    const isUp = data.current_value > data.previous_value
    const isGoodTrend = (data.metric_type === 'ppm' || data.metric_type === 'arrears')
      ? !isUp
      : isUp

    const Icon = isUp ? TrendingUp : TrendingDown
    const color = isGoodTrend ? 'text-green-500' : 'text-red-500'

    return <Icon className={`h-5 w-5 ${color}`} />
  }

  const formatValue = (value: number) => {
    if (data.metric_type === 'arrears') {
      return formatCurrency(value)
    } else if (data.metric_type === 'ppm') {
      return Math.round(value).toLocaleString()
    } else {
      return `${value.toFixed(1)}%`
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b bg-muted/30">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-foreground">{data.supplier_name}</h2>
              <p className="text-sm text-muted-foreground mt-1">{getMetricLabel()} - Detailed Breakdown</p>
            </div>
            <button
              onClick={onClose}
              className="ml-4 p-2 hover:bg-muted rounded-lg transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Performance Summary */}
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-card p-3 rounded-lg border">
              <p className="text-xs text-muted-foreground mb-1">Current Performance</p>
              <div className="flex items-center gap-2">
                <p className={`text-xl font-bold ${getMetricColor()}`}>
                  {data.current_value === null ? 'N/A' : formatValue(data.current_value)}
                </p>
                {getTrendIcon()}
              </div>
            </div>

            {data.target !== undefined && (
              <div className="bg-card p-3 rounded-lg border">
                <p className="text-xs text-muted-foreground mb-1">Target</p>
                <p className="text-xl font-bold text-muted-foreground">
                  {formatValue(data.target)}
                </p>
              </div>
            )}

            {data.previous_value !== undefined && (
              <div className="bg-card p-3 rounded-lg border">
                <p className="text-xs text-muted-foreground mb-1">Previous Month</p>
                <p className="text-xl font-bold text-muted-foreground">
                  {formatValue(data.previous_value)}
                </p>
              </div>
            )}

            {data.previous_value !== undefined && data.current_value !== null && (
              <div className="bg-card p-3 rounded-lg border">
                <p className="text-xs text-muted-foreground mb-1">Change</p>
                <p className={`text-xl font-bold ${
                  data.current_value > data.previous_value
                    ? (data.metric_type === 'ppm' || data.metric_type === 'arrears' ? 'text-red-600' : 'text-green-600')
                    : (data.metric_type === 'ppm' || data.metric_type === 'arrears' ? 'text-green-600' : 'text-red-600')
                }`}>
                  {data.current_value > data.previous_value ? '+' : ''}
                  {((data.current_value - data.previous_value) / data.previous_value * 100).toFixed(1)}%
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Summary Stats */}
          {data.summary && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <Package className="h-5 w-5" />
                Summary Statistics
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {data.summary.total_pos !== undefined && (
                  <div className="bg-muted/50 p-3 rounded">
                    <p className="text-xs text-muted-foreground">Total Purchase Orders</p>
                    <p className="text-lg font-bold">{data.summary.total_pos.toLocaleString()}</p>
                  </div>
                )}
                {data.summary.total_lines !== undefined && (
                  <div className="bg-muted/50 p-3 rounded">
                    <p className="text-xs text-muted-foreground">Total PO Lines</p>
                    <p className="text-lg font-bold">{data.summary.total_lines.toLocaleString()}</p>
                  </div>
                )}
                {data.summary.total_value !== undefined && (
                  <div className="bg-muted/50 p-3 rounded">
                    <p className="text-xs text-muted-foreground">Total Value</p>
                    <p className="text-lg font-bold">{formatCurrency(data.summary.total_value)}</p>
                  </div>
                )}
                {data.summary.affected_parts !== undefined && (
                  <div className="bg-muted/50 p-3 rounded">
                    <p className="text-xs text-muted-foreground">Affected Parts</p>
                    <p className="text-lg font-bold">{data.summary.affected_parts.toLocaleString()}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* NCRs Detail (for PPM) */}
          {data.details?.ncrs && data.details.ncrs.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-500" />
                Non-Conformance Reports ({data.details.ncrs.length})
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-3 py-2 text-left">NCR #</th>
                      <th className="px-3 py-2 text-left">Part Number</th>
                      <th className="px-3 py-2 text-right">Qty Rejected</th>
                      <th className="px-3 py-2 text-left">Date Raised</th>
                      <th className="px-3 py-2 text-left">Status</th>
                      <th className="px-3 py-2 text-left">Severity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.details.ncrs.map((ncr, idx) => (
                      // CR-7276: the row opens the NCR; keyed on the NCR, not its position.
                      <tr
                        key={ncr.ncr_id || `${ncr.ncr_number}-${idx}`}
                        className={`border-b hover:bg-muted/30 ${ROW_DRILL_CLASS}`}
                        {...rowDrillProps(getRowDrillUrl({ ncr_id: ncr.ncr_id, ncr_number: ncr.ncr_number }))}
                      >
                        <td className="px-3 py-2 font-mono text-xs">{ncr.ncr_number}</td>
                        <td className="px-3 py-2 font-mono text-xs">{ncr.part_number}</td>
                        <td className="px-3 py-2 text-right font-semibold">{ncr.qty_rejected.toLocaleString()}</td>
                        <td className="px-3 py-2">{ncr.date_raised ? formatDate(ncr.date_raised) : '—'}</td>
                        <td className="px-3 py-2">
                          <span className={`px-2 py-1 rounded text-xs ${
                            ncr.status === 'closed' ? 'bg-green-100 text-green-800' :
                            ncr.status === 'open' ? 'bg-red-100 text-red-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {ncr.status}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          <span className={`px-2 py-1 rounded text-xs ${
                            ncr.severity === 'critical' ? 'bg-red-100 text-red-800' :
                            ncr.severity === 'major' ? 'bg-orange-100 text-orange-800' :
                            'bg-blue-100 text-blue-800'
                          }`}>
                            {ncr.severity || 'N/A'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Late Deliveries Detail (for OTIF/OTD) */}
          {data.details?.late_deliveries && data.details.late_deliveries.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <Calendar className="h-5 w-5 text-orange-500" />
                Late Deliveries ({data.details.late_deliveries.length})
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-3 py-2 text-left">PO Number</th>
                      <th className="px-3 py-2 text-left">Part Number</th>
                      <th className="px-3 py-2 text-left">Due Date</th>
                      <th className="px-3 py-2 text-left">Actual Date</th>
                      <th className="px-3 py-2 text-right">Days Late</th>
                      <th className="px-3 py-2 text-right">Quantity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.details.late_deliveries.map((delivery, idx) => (
                      // CR-7276: this payload carries no PO-line id, so the row opens the PO header.
                      <tr
                        key={`${delivery.po_number}-${delivery.part_number}-${idx}`}
                        className={`border-b hover:bg-muted/30 ${getRowDrillUrl(delivery) ? ROW_DRILL_CLASS : ''}`}
                        {...rowDrillProps(getRowDrillUrl(delivery))}
                      >
                        <td className="px-3 py-2 font-mono text-xs">{delivery.po_number}</td>
                        <td className="px-3 py-2 font-mono text-xs">{delivery.part_number}</td>
                        <td className="px-3 py-2">{delivery.due_date ? formatDate(delivery.due_date) : '—'}</td>
                        <td className="px-3 py-2">{delivery.actual_date ? formatDate(delivery.actual_date) : '—'}</td>
                        <td className="px-3 py-2 text-right">
                          <span className="text-red-600 font-semibold">{delivery.days_late}</span>
                        </td>
                        <td className="px-3 py-2 text-right">{delivery.qty.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Arrears Detail */}
          {data.details?.arrears_lines && data.details.arrears_lines.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-yellow-500" />
                Overdue PO Lines ({data.details.arrears_lines.length})
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-3 py-2 text-left">PO Number</th>
                      <th className="px-3 py-2 text-left">Part Number</th>
                      <th className="px-3 py-2 text-right">Qty Outstanding</th>
                      <th className="px-3 py-2 text-right">Days Overdue</th>
                      <th className="px-3 py-2 text-right">Value</th>
                      <th className="px-3 py-2 text-left">Due Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.details.arrears_lines.map((line, idx) => (
                      // CR-7276: this payload carries no PO-line id, so the row opens the PO header.
                      <tr
                        key={`${line.po_number}-${line.part_number}-${idx}`}
                        className={`border-b hover:bg-muted/30 ${getRowDrillUrl(line) ? ROW_DRILL_CLASS : ''}`}
                        {...rowDrillProps(getRowDrillUrl(line))}
                      >
                        <td className="px-3 py-2 font-mono text-xs">{line.po_number}</td>
                        <td className="px-3 py-2 font-mono text-xs">{line.part_number}</td>
                        <td className="px-3 py-2 text-right">{line.qty_outstanding.toLocaleString()}</td>
                        <td className="px-3 py-2 text-right">
                          <span className={`font-semibold ${
                            line.days_overdue > 30 ? 'text-red-600' :
                            line.days_overdue > 14 ? 'text-orange-600' :
                            'text-yellow-600'
                          }`}>
                            {line.days_overdue}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right font-semibold">{formatCurrency(line.value, line.currency)}</td>
                        <td className="px-3 py-2">{line.due_date ? formatDate(line.due_date) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* No Details Available */}
          {!data.details?.ncrs && !data.details?.late_deliveries && !data.details?.arrears_lines && (
            <div className="text-center py-12">
              <AlertTriangle className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">
                Detailed breakdown data not available for this supplier.
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                This feature will be available once backend drill-down endpoints are implemented.
              </p>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="px-6 py-4 border-t bg-muted/30">
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              Click outside or press ESC to close
            </div>
            <div className="flex gap-2">
              <button className="px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90 transition-colors">
                Create NCR
              </button>
              <button className="px-4 py-2 border border-border rounded hover:bg-muted transition-colors">
                Send Alert
              </button>
              <button
                onClick={onClose}
                className="px-4 py-2 border border-border rounded hover:bg-muted transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
